import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';

import { PermissionsService } from '../crm/permissions.service';
import { PlanAccessService } from '../crm/plan-access.service';
import { AuthService } from './auth.service';

/**
 * Defense-in-depth for the nav's show/hide logic — blocks direct URL
 * navigation to a route the user has no relevant permission for, redirecting
 * to the dashboard instead of leaving them on a page that can only error
 * (RLS still blocks the underlying data regardless of this guard).
 */
export function permissionGuard(anyOf: readonly string[]): CanActivateFn {
  return () => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    return anyOf.some((key) => permissions.can(key)) ? true : router.createUrlTree(['/dashboard']);
  };
}

/** Like permissionGuard, but every key is needed (e.g. editing a campaign AND sending a broadcast). */
export function allPermissionsGuard(allOf: readonly string[]): CanActivateFn {
  return () => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    return allOf.every((key) => permissions.can(key)) ? true : router.createUrlTree(['/dashboard']);
  };
}

/**
 * /settings: anyone holding a permission behind one of its tabs (`anyOf` is
 * SETTINGS_ANY_PERMISSION, the list the sidebar and the page share), plus the
 * clinic owner, whose role may grant no settings at all. The owner still needs
 * Settings › Plan to see when the subscription ends and how to renew; the page
 * then shows them only the tabs they can use, which can be just Plan.
 */
export function settingsGuard(anyOf: readonly string[]): CanActivateFn {
  const byPermission = permissionGuard(anyOf);
  return (route, state) => (inject(PlanAccessService).isOwner() ? true : byPermission(route, state));
}

/**
 * /automation is the workflow list, but Automation and Broadcasts are separate
 * plan modules: someone with Broadcasts only lands on the Broadcasts tab
 * instead of being bounced to the dashboard.
 */
export const automationHomeGuard: CanActivateFn = () => {
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  if (permissions.can('automation.read')) return true;
  return router.createUrlTree([permissions.can('broadcasts.read') ? '/automation/broadcasts' : '/dashboard']);
};

/**
 * Blocks the CRM app when the signed-in user holds no permissions at all (e.g.
 * CRM login access but no role yet) — sends them to a dedicated "no access"
 * page instead of an empty shell.
 */
export const crmAccessGuard: CanActivateFn = () => {
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  return permissions.hasAnyPermission() ? true : router.createUrlTree(['/no-access']);
};

/**
 * Keeps users who DO have permissions off the no-access page. A clinic without
 * an active plan also has no permissions (my_permissions() strips them), but it
 * belongs on the /plans gate, as the shell sends it — not on "no access".
 */
export const noAccessOnlyGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const planAccess = inject(PlanAccessService);
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  if (!auth.currentUser()?.is_superAdmin && !planAccess.hasActivePlan()) return router.createUrlTree(['/plans']);
  return permissions.hasAnyPermission() ? router.createUrlTree(['/dashboard']) : true;
};

/**
 * Gates the CRM app on the clinic having an ACTIVE plan — sends clinics with no
 * (or lapsed) subscription to the /plans screen. Platform super admins bypass
 * (they may have no clinic/plan of their own).
 */
export const planGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const planAccess = inject(PlanAccessService);
  const router = inject(Router);

  if (auth.currentUser()?.is_superAdmin || planAccess.hasActivePlan()) {
    return true;
  }
  return router.createUrlTree(['/plans']);
};

/**
 * planGuard again for every navigation INSIDE the shell. The shell's canActivate
 * runs only on the way in; this closes the gap where access is lost mid-session
 * and PlanAccessService's move to /plans is overtaken by a click on a nav link.
 */
export const planChildGuard: CanActivateChildFn = (route, state) => planGuard(route, state);

/** Keeps clinics that DO have a plan (and super admins) off the /plans screen. */
export const noPlanOnlyGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const planAccess = inject(PlanAccessService);
  const router = inject(Router);

  return auth.currentUser()?.is_superAdmin || planAccess.hasActivePlan() ? router.createUrlTree(['/dashboard']) : true;
};

/**
 * Super-admin-only admin screens (e.g. Architecture). Platform role holders can
 * also sign into Admin, so authGuard alone is not enough. This only hides the
 * page — anything sensitive on it must come from a server-checked endpoint,
 * because the lazy chunk itself is downloadable without signing in.
 */
export const superAdminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.currentUser()?.is_superAdmin ? true : router.createUrlTree(['/dashboard']);
};
